import { Directive, EventEmitter, HostListener, Output, Optional, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBarRef } from '@angular/material/snack-bar';

/**
 * Директива для закрытия модальных окон по нажатию Escape
 * 
 * Использование:
 * 1. Для Material Dialog (автоматически):
 *    <div appCloseOnEscape>...</div>
 * 
 * 2. Для Material Snackbar (автоматически):
 *    <div appCloseOnEscape>...</div>
 * 
 * 3. С EventEmitter (для кастомных модальных окон):
 *    <div appCloseOnEscape (onEscape)="onClose()">...</div>
 *    <div appCloseOnEscape (onEscape)="toggleVisible()">...</div>
 * 
 * Директива работает глобально на уровне document, поэтому не требует tabindex
 */
@Directive({
  selector: '[appCloseOnEscape]',
  standalone: true
})
export class CloseOnEscapeDirective implements OnDestroy {
  /**
   * EventEmitter для обработки Escape
   * Используется для кастомных модальных окон
   */
  @Output() onEscape = new EventEmitter<void>();

  private escapeHandler = (event: KeyboardEvent) => {
    if (event.key === 'Escape' || event.keyCode === 27) {
      this.handleEscape(event);
    }
  };

  constructor(
    @Optional() private dialogRef: MatDialogRef<any>,
    @Optional() private snackBarRef: MatSnackBarRef<any>
  ) {
    // Добавляем глобальный обработчик на document
    document.addEventListener('keydown', this.escapeHandler, true);
  }

  ngOnDestroy(): void {
    // Удаляем глобальный обработчик при уничтожении директивы
    document.removeEventListener('keydown', this.escapeHandler, true);
  }

  private handleEscape(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Приоритет 1: Material Dialog
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }

    // Приоритет 2: Material Snackbar
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
      return;
    }

    // Приоритет 3: EventEmitter для кастомных модальных окон
    this.onEscape.emit();
  }
}
